import { apiUrl, currentUserId, currentUserQuery } from "./auth";
import { watchActive, writeNotice, type BoardFile, type BoardCard, type BoardWatch, type CardNotice } from "./atelier";
import type { CrmFiche } from "./types";

export type NotifyPerson = { id: number; name: string; mail: boolean; sms: boolean };

function headers(): HeadersInit {
  const id = currentUserId();
  return { "Content-Type": "application/json", ...(id ? { "X-User-Id": id } : {}) };
}

async function postNotify(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const res = await fetch(apiUrl("board-notify.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: headers(),
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Notification impossible."));
  }
  return data;
}

export async function loadNotifyPeople(ficheId: number): Promise<NotifyPerson[]> {
  const data = await postNotify({ action: "people", fiche_id: ficheId });
  const rows = Array.isArray(data.people) ? data.people : [];
  return rows.flatMap((row) => {
    const item = row as { id?: unknown; name?: unknown; mail?: unknown; sms?: unknown };
    const id = Number(item.id) || 0;
    const name = String(item.name || "").trim();
    if (id <= 0 || name === "") return [];
    return [{ id, name, mail: item.mail === true, sms: item.sms === true }];
  });
}

export function sendVeilCopy(rows: BoardWatch[], title: string): { label: string; detail: string } {
  const mail = rows.some((row) => row.mail);
  const sms = rows.some((row) => row.sms);
  const label = mail && sms ? "Envoi de l’e-mail et du SMS…" : sms ? "Envoi du SMS…" : "Envoi de l’e-mail…";
  const name = title.trim();
  return { label, detail: name ? `Carte « ${name} ».` : "Aux personnes cochées." };
}

export function SendVeil({ phase, label, detail }: { phase: "send" | "done"; label: string; detail: string }) {
  const trouble = phase === "done" && /non parti|pas d|Rien envoyé|impossible/i.test(label);
  return (
    <div className="crm-send-veil" role="status" aria-live="polite">
      <div className="crm-pop crm-send-card">
        {phase === "send" ? <span className="crm-browse-loading-spin" aria-hidden /> : (
          <span className={trouble ? "crm-send-mark is-bad" : "crm-send-mark"} aria-hidden>
            {trouble ? (
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path strokeLinecap="round" d="M8 8l8 8M16 8l-8 8" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5 10 17.5 19 7" />
              </svg>
            )}
          </span>
        )}
        <strong>{label}</strong>
        {detail ? <span className="crm-send-detail">{detail}</span> : null}
      </div>
    </div>
  );
}

export function NoticeLine({ notice }: { notice: CardNotice | null }) {
  if (!notice || (notice.names.length === 0 && notice.gaps.length === 0)) return null;
  return (
    <p className="mt-1 text-[11px] leading-snug">
      {notice.names.length > 0 ? <span className="font-semibold text-slate-600">Partie à {notice.names.join(", ")}</span> : null}
      {notice.gaps.length > 0 ? <span className="mt-0.5 block text-rose-700">{notice.gaps.join(" · ")}</span> : null}
    </p>
  );
}

export async function deliverNotice(
  fiche: CrmFiche,
  boardName: string,
  board: BoardFile,
  card: BoardCard,
  kind: "move" | "manual",
): Promise<{ board: BoardFile; hint: string }> {
  if (!watchActive(board.notify) || card.title.trim() === "") {
    return { board, hint: watchActive(board.notify) ? "" : "Coche quelqu’un dans Réglages." };
  }
  const column = board.columns.find((item) => item.id === card.columnId)?.title || "";
  const data = await postNotify({
    action: "send",
    kind,
    fiche_id: fiche.id,
    fiche_name: fiche.name,
    board: boardName,
    column,
    title: card.title.trim(),
    notify: board.notify.map((row: BoardWatch) => ({ id: row.id, mail: row.mail, sms: row.sms })),
  });
  const names = stringList(data.names);
  const gaps = stringList(data.gaps);
  const hint = [names.length > 0 ? `Partie à ${names.join(", ")}` : "", gaps.join(" · "), String(data.hint || "")].filter(Boolean).join(" — ");
  if (names.length === 0 && gaps.length === 0) return { board, hint };
  return {
    board: writeNotice(board, card.id, { names, gaps, at: new Date().toISOString() }),
    hint,
  };
}

function stringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => String(item || "").trim()).filter(Boolean);
}
